import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { EnvelopeIcon, LockClosedIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';

const features = [
  'Real-time satellite imagery analysis',
  'NDVI-based crop health monitoring',
  'Disease detection & alerts',
  'Variable Rate Application maps',
];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/dashboard';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[45%] relative overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-kenagri-900 via-kenagri-800 to-kenagri-900" />

        {/* Decorative elements */}
        <div className="absolute inset-0 pattern-grid opacity-20" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-kenagri-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-kenagri-400/10 rounded-full blur-3xl" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
          {/* Logo */}
          <div className="flex items-center mb-12">
            <div className="flex items-center justify-center w-12 h-12 rounded-2xl bg-gradient-to-br from-kenagri-400 to-kenagri-600 shadow-glow-green">
              <span className="text-2xl">🛰️</span>
            </div>
            <span className="ml-4 text-3xl font-bold text-white tracking-tight">KENAGRI</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-6">
            Smart Farming
            <br />
            <span className="text-kenagri-300">Powered by Satellites</span>
          </h1>

          <p className="text-lg text-white/70 mb-10 max-w-md">
            Monitor your crops in real-time with advanced satellite imagery and AI-powered analytics.
          </p>

          {/* Feature list */}
          <ul className="space-y-4">
            {features.map((feature, i) => (
              <li key={i} className="flex items-center text-white/80">
                <CheckCircleIcon className="h-5 w-5 text-kenagri-400 mr-3 flex-shrink-0" />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-8 lg:p-12 bg-gray-50">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-kenagri-500 to-kenagri-600 shadow-glow-green">
                <span className="text-xl">🛰️</span>
              </div>
              <span className="ml-3 text-2xl font-bold text-gray-900">KENAGRI</span>
            </div>
          </div>

          {/* Form header */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900">Welcome back</h2>
            <p className="mt-2 text-gray-600">Sign in to your account to continue</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="Email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              autoComplete="email"
              leftIcon={<EnvelopeIcon className="h-5 w-5" />}
            />

            <Input
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
              leftIcon={<LockClosedIcon className="h-5 w-5" />}
              rightIcon={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="hover:text-gray-600 transition-colors"
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              }
            />

            <div className="flex items-center justify-between">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-kenagri-600 border-gray-300 rounded focus:ring-kenagri-500 transition-colors"
                />
                <span className="ml-2 text-sm text-gray-600">Remember me</span>
              </label>
              <a href="#" className="text-sm text-kenagri-600 hover:text-kenagri-700 font-medium transition-colors">
                Forgot password?
              </a>
            </div>

            <Button
              type="submit"
              loading={isLoading}
              fullWidth
              size="lg"
            >
              Sign in
            </Button>
          </form>

          {/* Sign up link */}
          <p className="mt-8 text-center text-gray-600">
            Don't have an account?{' '}
            <Link to="/register" className="text-kenagri-600 hover:text-kenagri-700 font-semibold transition-colors">
              Create one
            </Link>
          </p>

          {/* Demo hint */}
          <div className="mt-8 p-4 rounded-xl bg-kenagri-50 border border-kenagri-100">
            <p className="text-sm text-kenagri-700 text-center">
              Demo: Use any email and password to create an account
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
